import React, { useState } from "react";
import { Section } from "components/section";
import { type Customer } from "lib/sanity";
import { Text, Box } from "@chakra-ui/react";
import Marquee from "react-fast-marquee";
import { getCustomerLogoUrl } from "utils";
import Image from "next/image";

const MARQUEE_CONFIG = {
  speed: 20,
  gradientWidth: "100px",
} as const;

type AgentsStackedLogosProps = {
  customers: Customer[];
};

export function AgentsStackedLogos({ customers }: AgentsStackedLogosProps) {
  const [isHovered, setIsHovered] = useState(false);

  const filteredCustomers = customers.filter(Boolean);

  // Desktop: 2 rows
  const halfLength = Math.ceil(filteredCustomers.length / 2);
  const firstRowDesktop = filteredCustomers.slice(0, halfLength);
  const secondRowDesktop = filteredCustomers.slice(halfLength);

  // Mobile: 3 rows
  const thirdLength = Math.floor(filteredCustomers.length / 3);
  const firstRowMobile = filteredCustomers.slice(0, thirdLength);
  const secondRowMobile = filteredCustomers.slice(thirdLength, thirdLength * 2);
  const thirdRowMobile = filteredCustomers.slice(thirdLength * 2);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const renderMarqueeRow = (customers: Customer[], size: "small" | "large", direction?: "right") => {
    return (
      <Marquee
        play={!isHovered}
        speed={MARQUEE_CONFIG.speed}
        gradientWidth={MARQUEE_CONFIG.gradientWidth}
        {...(direction ? { direction } : {})}
      >
        {customers.map((customer) => (
          <Logo key={customer.slug} customer={customer} size={size} />
        ))}
        {customers.map((customer) => (
          <Logo key={`${customer.slug}-dup`} customer={customer} size={size} />
        ))}
      </Marquee>
    );
  };

  return (
    <Section fullWidth noContainerPx>
      <Text textStyle="headline2" textAlign="center" mx="auto" mb={{ base: "bigger", md: "biggest" }}>
        The world’s most innovative companies choose Hightouch
      </Text>

      <Box
        display="flex"
        flexDirection="column"
        gap={{ base: "smaller", md: "big" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Mobile: 3 rows */}
        <Box display={{ base: "flex", md: "none" }} flexDirection="column" gap="big">
          {renderMarqueeRow(firstRowMobile, "small")}
          {renderMarqueeRow(secondRowMobile, "small", "right")}
          {renderMarqueeRow(thirdRowMobile, "small")}
        </Box>

        {/* Desktop: 2 rows */}
        <Box display={{ base: "none", md: "flex" }} flexDirection="column" gap="big">
          {renderMarqueeRow(firstRowDesktop, "large")}
          {renderMarqueeRow(secondRowDesktop, "large", "right")}
        </Box>
      </Box>
    </Section>
  );
}

type LogoProps = { customer: Customer; size?: "small" | "medium" | "large" };

function Logo({ customer, size = "medium" }: LogoProps) {
  const sizeConfig = {
    small: { width: 100, height: 50, maxW: "100px", maxH: "36px", imageWidth: 300, minW: "100px", h: "50px" },
    medium: { width: 120, height: 60, maxW: "100px", maxH: "36px", imageWidth: 300, minW: "120px", h: "60px" },
    large: { width: 150, height: 75, maxW: "130px", maxH: "50px", imageWidth: 400, minW: "150px", h: "75px" },
  };

  const config = sizeConfig[size];

  return (
    <Box
      display="flex"
      pos="relative"
      alignItems="center"
      justifyContent="center"
      p={{ base: "smaller", md: "small" }}
      mr="small"
      borderRadius="tiny"
      border="1px solid"
      borderColor="new.blue.300"
      userSelect="none"
      h={config.h}
      w={config.minW}
      minW={config.minW}
      flexShrink={0}
    >
      <Image
        src={`${getCustomerLogoUrl(customer, "dark")}?w=${config.imageWidth}`}
        alt={`${customer.name}.`}
        width={config.width}
        height={config.height}
        sizes={config.minW}
        style={{
          objectFit: "contain",
          objectPosition: "center",
          width: "100%",
          height: "100%",
          maxWidth: config.maxW,
          maxHeight: config.maxH,
          filter: "brightness(0)",
          opacity: 0.9,
        }}
      />
    </Box>
  );
}
